/**
 * Die Papiere aus „Vollmachten u. Verfügungen" — Excel R279–R299, Stand
 * 25.08.2026.
 *
 * **Eine Liste für drei Stellen.** Die Langform (Beratermodus) braucht je
 * Papier ein Statusfeld UND einen Datei-Slot, die Kurzform nur das Statusfeld,
 * und die Slot-Tabelle des Servers (`RISIKOANALYSE_UPLOAD_SLOTS` in apps/api)
 * muss dieselben Datei-Keys, Ordner und Obergrenzen führen — sonst nimmt der
 * Upload-Endpunkt die Datei mit 400 nicht an. Drei handgepflegte Listen für
 * dieselben dreizehn Zeilen liefen unweigerlich auseinander; deshalb steht die
 * Reihenfolge, die Beschriftung und der Schlüssel hier und nur hier.
 * `risikoanalyse-upload.slots.spec.ts` (apps/api) vergleicht das Ergebnis mit
 * der Server-Tabelle und wird bei Drift rot.
 *
 * **Warum dreizehn und nicht zwanzig.** Sieben Zeilen der Excel sind mit
 * „nur PK" gekennzeichnet — Vorsorge-, Patienten-, Betreuungs-, Sorgerechts-,
 * Trauer- und Haus-/Großtierverfügung sowie die Schweigepflichtentbindung.
 * Die ersten sechs führt KMVS längst als eigenes Datenmodell am Kontakt
 * (`ContactVerfuegung`, je Verfügung mit Bevollmächtigtem und Verknüpfung in
 * dessen Akte). Sie hier ein zweites Mal zu erfassen hiesse, denselben
 * Sachverhalt an zwei Stellen zu pflegen, die nichts voneinander wissen — und
 * diese Analyse ist eine Firmenanalyse, in der eine Patientenverfügung ohnehin
 * nichts zu suchen hat. Entscheidung des PO vom 28.08.2026.
 */
/** Ordnerwurzel aller Vollmacht-Scans unterhalb von „Risikoanalyse/". */
export const VOLLMACHT_ORDNER_WURZEL = "Vollmachten";
/**
 * Dateien je Papier. Drei statt einer: Neben der unterschriebenen Fassung
 * laufen in der Praxis Nachträge, eine Widerrufserklärung oder die
 * gegengezeichnete Zweitschrift auf.
 */
export const VOLLMACHT_MAX_DATEIEN = 3;
/** Datei-Slot-Key zu einem Papier. */
export const vollmachtScanKey = (key) => `${key}_scan`;
/** Vollständiges Ordnersegment eines Papiers (wie in der Server-Tabelle). */
export const vollmachtOrdner = (dokument) => `${VOLLMACHT_ORDNER_WURZEL}/${dokument.ordner}`;
/**
 * Reihenfolge wie in der Excel (R280–R299, ohne die „nur PK"-Zeilen).
 *
 * Die `key`-Werte sind Speicherwerte und dürfen NIE umbenannt werden — sie
 * stehen in gespeicherten Antwortsätzen und, als `<key>_scan`, in
 * `Document.riskAnalysisFieldKey` jeder hochgeladenen Datei.
 */
export const VOLLMACHT_DOKUMENTE = Object.freeze([
    {
        key: "vollmacht_kundenerstinfo",
        titel: "Kundenerstinformation und Datenschutzerklärung",
        anzeigeBegriff: "Kundenerstinformation und Datenschutzerklärung",
        ordner: "Kundenerstinformation",
        pflicht: true,
    },
    {
        key: "vollmacht_werbeeinwilligung",
        titel: "Werbeeinwilligung",
        anzeigeBegriff: "Werbeeinwilligung",
        ordner: "Werbeeinwilligung",
        pflicht: true,
    },
    {
        key: "vollmacht_geldempfang",
        titel: "Vollmacht Entgegennahme u. Auszahlung von Geldern",
        anzeigeBegriff: "Vollmacht Entgegennahme u. Auszahlung von Geldern",
        ordner: "Entgegennahme und Auszahlung von Geldern",
    },
    {
        key: "vollmacht_angebotseinholung",
        titel: "Vollmacht Angebotseinholung",
        anzeigeBegriff: "Vollmacht Angebotseinholung",
        ordner: "Angebotseinholung",
    },
    {
        key: "vollmacht_auskunft_fremdvertraege",
        titel: "Vollmacht Auskunftserteilung Fremdverträge",
        anzeigeBegriff: "Vollmacht Auskunftserteilung Fremdverträge",
        ordner: "Auskunftserteilung Fremdverträge",
    },
    {
        key: "vollmacht_betreuung_fremdvertraege",
        titel: "Vollmacht Betreuungsauftrag für Fremdverträge",
        anzeigeBegriff: "Vollmacht Betreuungsauftrag Fremdverträge",
        ordner: "Betreuungsauftrag Fremdverträge",
        hinweis: "Ventil",
    },
    {
        key: "vollmacht_maklerauftrag",
        titel: "Maklerauftrag, Versicherungsmaklervertrag",
        anzeigeBegriff: "Maklerauftrag",
        ordner: "Maklerauftrag",
    },
    {
        key: "vollmacht_maklervollmacht",
        titel: "Maklervollmacht, allgemeine Vertretungsvollmacht",
        anzeigeBegriff: "Maklervollmacht",
        ordner: "Maklervollmacht",
    },
    {
        key: "vollmacht_schaden_vertretung",
        titel: "Auftrags- und Vertretungsvereinbarung Schaden",
        anzeigeBegriff: "Auftrags- und Vertretungsvereinbarung",
        auswahlLabel: "Auftrags- und Vertretungsvereinbarung (Schaden)",
        ordner: "Auftrags- und Vertretungsvereinbarung Schaden",
    },
    {
        key: "vollmacht_schadenabwicklung",
        titel: "Schadenabwicklungsvollmacht",
        anzeigeBegriff: "Schadenabwicklungsvollmacht",
        ordner: "Schadenabwicklungsvollmacht",
    },
    {
        key: "vollmacht_geheimhaltung",
        titel: "Geheimhaltungsvereinbarung",
        anzeigeBegriff: "Geheimhaltungsvereinbarung",
        ordner: "Geheimhaltungsvereinbarung",
    },
    {
        key: "vollmacht_beteiligungssuche",
        titel: "Vollmacht Beteiligungssuche",
        anzeigeBegriff: "Vollmacht Beteiligungssuche",
        ordner: "Beteiligungssuche",
        hinweis: "nur Firmenkunden",
    },
    {
        key: "vollmacht_digitalnachlass",
        titel: "Digitaler Nachlass / Digitalvollmacht",
        anzeigeBegriff: "Digitaler Nachlass / Digitalvollmacht",
        ordner: "Digitaler Nachlass",
    },
]);
/**
 * Die Vollmacht, die bei jeder Kontaktanlage vorausgewählt ist: Ohne
 * Erstinformation (§ 15 VersVermV) und Datenschutzerklärung darf der Makler
 * nicht tätig werden.
 */
export const VOLLMACHT_STANDARD_KEY = "vollmacht_kundenerstinfo";
/** Beschriftung einer Vollmacht im Auswahl-Dropdown der Kontaktanlage. */
export const vollmachtAuswahlLabel = (dokument) => dokument.auswahlLabel ?? dokument.anzeigeBegriff;
/** Katalogeintrag zu einem Key — `undefined` bei unbekanntem Key. */
export const vollmachtDokument = (key) => VOLLMACHT_DOKUMENTE.find((dokument) => dokument.key === key);
